import { useCallback, useEffect, useMemo, useState } from 'react'
import { useAtomValue } from 'jotai'
import { SidebarResultList, SidebarResultListItem } from 'src/v2/components/sidebar-result-list'
import { InsightEmpty } from 'src/views/insight-empty'
import { getEntityLink, getParcelLink } from 'src/v2/utils/entity'
import { SEARCH_RESULT_TYPE_ENUM } from 'src/common-services'
import { useMapExplorerNavigation } from 'src/views/map-explorer/sidebar/search/use-navigation'
import { useMapExplorerChoice } from 'src/views/map-explorer/sidebar/search/use-choice'
import { SearchResultsToggle } from 'src/views/map-explorer/sidebar/panels/results/search-results-toggle'
import {
  buildSearchResults,
  checkIfResultsAreCompletelyEmpty,
  checkIfNearbyResultsAreEmpty,
  checkIfParcelResultsAreEmpty,
} from 'src/views/map-explorer/sidebar/panels/results/search-result-items'
import { Colors } from 'src/components/theme'
import { EmptyResultItem } from 'src/views/map-explorer/sidebar/panels/results/empty-result-item'

import { searchResults } from 'src/v2/state/map-explorer/search'
import * as selectorAtoms from 'src/v2/state/map-explorer/selector'
import { COMMANDAI_IDS_MAP } from 'src/constants/command-ai-ids'

const canShowSection = (supportedSelections: string[] | undefined, section: string) => {
  return !supportedSelections || (supportedSelections && supportedSelections.includes(section))
}

const noPaginationProps = {
  page: 0,
  setPage: () => {},
  sortOptions: [],
  totalPages: 0,
}

export const MapExplorerSearchResults = () => {
  const results = useAtomValue(searchResults)
  const { selectLocation, selectAddress, selectBrand } = useMapExplorerNavigation()
  const { chooseLocation, chooseAddress } = useMapExplorerChoice()
  const supportedSelections = useAtomValue(selectorAtoms.supportedSelections)
  const [activeResultsType, setActiveResultsType] = useState<'nearby' | 'parcels'>('nearby')

  const searchResultsItems = useMemo(() => {
    if (!results) {
      return null
    }
    return buildSearchResults(results, supportedSelections)
  }, [results, supportedSelections])

  const onSetResultsType = (type: 'nearby' | 'parcels') => {
    setActiveResultsType(type)
  }

  // Used for navigation to a selected result
  const onNavigateByItem = async (item: SidebarResultListItem) => {
    switch (item.type) {
      case SEARCH_RESULT_TYPE_ENUM.Enum.location:
      case SEARCH_RESULT_TYPE_ENUM.Enum['shopping center']:
        await selectLocation(item.id)
        break
      case SEARCH_RESULT_TYPE_ENUM.Enum.address:
        await selectAddress(item.id)
        break
      case SEARCH_RESULT_TYPE_ENUM.Enum.parcel:
        const parcelLink = getParcelLink(item.id)
        window.open(parcelLink, '_blank')
        break
      case SEARCH_RESULT_TYPE_ENUM.Enum.owner:
        const entityLink = getEntityLink(item.id)
        window.open(entityLink, '_blank')
      case SEARCH_RESULT_TYPE_ENUM.Enum.brand:
        await selectBrand(item.id)
        break
      default:
        break
    }
  }
  // Used for selection of a result
  const onChooseItem = async (item: SidebarResultListItem) => {
    switch (item.type) {
      case SEARCH_RESULT_TYPE_ENUM.Enum.location:
        chooseLocation(item.id)
        break
      case SEARCH_RESULT_TYPE_ENUM.Enum.address:
        chooseAddress(item.id)
        break
      default:
        break
    }
  }

  // If in entity selector, we want to choose the item
  // Otherwise, we want to navigate to the item
  const onClickItem = useCallback(
    async (item: SidebarResultListItem) => {
      if (Boolean(supportedSelections)) {
        await onChooseItem(item)
      } else {
        await onNavigateByItem(item)
      }
    },
    [supportedSelections, onChooseItem, onNavigateByItem]
  )

  // Default search results type based on availablity
  useEffect(() => {
    if (!results) {
      return
    }
    if (!checkIfNearbyResultsAreEmpty(results, supportedSelections)) {
      setActiveResultsType('nearby')
    } else if (!checkIfParcelResultsAreEmpty(results)) {
      setActiveResultsType('parcels')
    }
  }, [results, supportedSelections])

  if (!results) {
    return null
  }
  if (checkIfResultsAreCompletelyEmpty(results)) {
    return <InsightEmpty title="No results found" message="Try changing your search criteria." />
  }

  return (
    <>
      <SearchResultsToggle
        activeResultsType={activeResultsType}
        setResultsType={onSetResultsType}
        hasNearbyResults={!checkIfNearbyResultsAreEmpty(results, supportedSelections)}
        hasParcelResults={!checkIfParcelResultsAreEmpty(results)}
        nearbyCount={
          (searchResultsItems?.nearby?.locations?.length || 0) +
          (searchResultsItems?.nearby?.addresses?.length || 0) +
          (searchResultsItems?.nearby?.shoppingCenter?.length || 0) +
          (searchResultsItems?.nearby?.brands?.length || 0) +
          (searchResultsItems?.nearby?.owners?.length || 0) +
          (searchResultsItems?.nearby?.parcels?.length || 0)
        }
        parcelCount={
          (searchResultsItems?.byParcels?.locations?.length || 0) +
          (searchResultsItems?.byParcels?.shoppingCenters?.length || 0) +
          (searchResultsItems?.byParcels?.parcels?.length || 0) +
          (searchResultsItems?.byParcels?.owners?.length || 0)
        }
      />
      <div>
        {searchResultsItems?.nearby && activeResultsType === 'nearby' && (
          <>
            {canShowSection(supportedSelections, 'SELECTED_LOCATION') && (
              <SidebarResultList
                id={COMMANDAI_IDS_MAP.mapExplorer['locations-results-list']}
                bgColor={Colors.bgLight}
                {...noPaginationProps}
                title={`Locations (${searchResultsItems.nearby.locations.length + searchResultsItems.nearby.addresses.length})`}
                items={[...searchResultsItems.nearby.locations, ...searchResultsItems.nearby.addresses]}
                onClickItem={onClickItem}
                collapsible={
                  searchResultsItems.nearby.locations.length + searchResultsItems.nearby.addresses.length > 4
                }
                customEmptyMessage={<EmptyResultItem title="Locations" />}
              />
            )}

            {canShowSection(supportedSelections, 'SELECTED_SHOPPING_CENTER') && (
              <SidebarResultList
                id={COMMANDAI_IDS_MAP.mapExplorer['shopping-centers-results-list']}
                bgColor={Colors.bgLight}
                {...noPaginationProps}
                title={`Shopping Centers (${searchResultsItems.nearby.shoppingCenter.length})`}
                items={searchResultsItems.nearby.shoppingCenter}
                onClickItem={onClickItem}
                collapsible={searchResultsItems.nearby.shoppingCenter.length > 4}
                customEmptyMessage={<EmptyResultItem title="Shopping Centers" />}
              />
            )}
            {canShowSection(supportedSelections, 'SELECTED_ADDRESS') && (
              <SidebarResultList
                id={COMMANDAI_IDS_MAP.mapExplorer['address-results-list']}
                bgColor={Colors.bgLight}
                {...noPaginationProps}
                title={`Other (${searchResultsItems.nearby.other.length})`}
                items={searchResultsItems.nearby.other}
                onClickItem={onClickItem}
                collapsible={searchResultsItems.nearby.other.length > 2}
                customEmptyMessage={<EmptyResultItem title="Other" />}
              />
            )}
            {canShowSection(supportedSelections, 'SELECTED_BRAND') && (
              <SidebarResultList
                bgColor={Colors.bgLight}
                {...noPaginationProps}
                title={`Brands (${searchResultsItems.nearby.brands.length})`}
                items={searchResultsItems.nearby.brands}
                onClickItem={onClickItem}
                collapsible={searchResultsItems.nearby.brands.length > 2}
                customEmptyMessage={<EmptyResultItem title="Brands" />}
                hideEmpty
              />
            )}
            {canShowSection(supportedSelections, 'SELECTED_PARCEL') && (
              <SidebarResultList
                bgColor={Colors.bgLight}
                {...noPaginationProps}
                title={`Parcels (${searchResultsItems.nearby.parcels.length})`}
                items={searchResultsItems.nearby.parcels}
                onClickItem={onClickItem}
                collapsible={searchResultsItems.nearby.parcels.length > 2}
                customEmptyMessage={<EmptyResultItem title="Parcels" />}
                hideEmpty
              />
            )}
            {canShowSection(supportedSelections, 'SELECTED_OWNER') && (
              <SidebarResultList
                id={COMMANDAI_IDS_MAP.mapExplorer['other-results-list']}
                bgColor={Colors.bgLight}
                {...noPaginationProps}
                title={`Owners (${searchResultsItems.nearby.owners.length})`}
                items={searchResultsItems.nearby.owners}
                onClickItem={onClickItem}
                collapsible={searchResultsItems.nearby.owners.length > 2}
                customEmptyMessage={<EmptyResultItem title="Owners" />}
                hideEmpty
              />
            )}
          </>
        )}
        {searchResultsItems?.byParcels && activeResultsType === 'parcels' && (
          <>
            {canShowSection(supportedSelections, 'SELECTED_LOCATION') && (
              <SidebarResultList
                id={COMMANDAI_IDS_MAP.mapExplorer['locations-results-list']}
                bgColor={Colors.bgLight}
                {...noPaginationProps}
                title={`Locations (${searchResultsItems.byParcels.locations.length})`}
                items={searchResultsItems.byParcels.locations}
                onClickItem={onClickItem}
                collapsible={searchResultsItems.byParcels.locations.length > 4}
                customEmptyMessage={<EmptyResultItem title="Locations" />}
              />
            )}
            {canShowSection(supportedSelections, 'SELECTED_SHOPPING_CENTER') && (
              <SidebarResultList
                id={COMMANDAI_IDS_MAP.mapExplorer['shopping-centers-results-list']}
                bgColor={Colors.bgLight}
                {...noPaginationProps}
                title={`Shopping centers (${searchResultsItems.byParcels.shoppingCenters.length})`}
                items={searchResultsItems.byParcels.shoppingCenters}
                onClickItem={onClickItem}
                collapsible={searchResultsItems.byParcels.shoppingCenters.length > 4}
                customEmptyMessage={<EmptyResultItem title="Shopping Centers" />}
              />
            )}
            {canShowSection(supportedSelections, 'SELECTED_BRAND') && (
              <SidebarResultList
                bgColor={Colors.bgLight}
                {...noPaginationProps}
                title={`Brands (${searchResultsItems.byParcels.brands.length})`}
                items={searchResultsItems.byParcels.brands}
                onClickItem={onClickItem}
                collapsible={searchResultsItems.byParcels.brands.length > 2}
                customEmptyMessage={<EmptyResultItem title="Brands" />}
                hideEmpty
              />
            )}
            {canShowSection(supportedSelections, 'SELECTED_PARCEL') && (
              <SidebarResultList
                bgColor={Colors.bgLight}
                {...noPaginationProps}
                title={`Parcels (${searchResultsItems.byParcels.parcels.length})`}
                items={searchResultsItems.byParcels.parcels}
                onClickItem={onClickItem}
                collapsible={searchResultsItems.byParcels.parcels.length > 2}
                customEmptyMessage={<EmptyResultItem title="Parcels" />}
                hideEmpty
              />
            )}
            {canShowSection(supportedSelections, 'SELECTED_OWNER') && (
              <SidebarResultList
                bgColor={Colors.bgLight}
                {...noPaginationProps}
                title={`Owners (${searchResultsItems.byParcels.owners.length})`}
                items={searchResultsItems.byParcels.owners}
                onClickItem={onClickItem}
                collapsible={searchResultsItems.byParcels.owners.length > 2}
                customEmptyMessage={<EmptyResultItem title="Owners" />}
                hideEmpty
              />
            )}
          </>
        )}
      </div>
    </>
  )
}
